import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { mesConversations, conversation, envoyerMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, Send, ArrowLeft } from 'lucide-react';

const Messages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [convActive, setConvActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    mesConversations()
      .then(res => setConversations(res.data.conversations))
      .finally(() => setLoading(false));
  }, [user]);

  const ouvrirConversation = async (conv) => {
    setConvActive(conv);
    const userId = conv.expediteur_id === user.id ? conv.destinataire_id : conv.expediteur_id;
    const res = await conversation(userId);
    setMessages(res.data.messages);
  };

  const handleEnvoyer = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      const userId = convActive.expediteur_id === user.id
        ? convActive.destinataire_id
        : convActive.expediteur_id;
      await envoyerMessage({
        destinataire_id: userId,
        annonce_id: convActive.annonce_id,
        contenu: newMessage
      });
      setNewMessage('');
      const res = await conversation(userId);
      setMessages(res.data.messages);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-white text-2xl font-black">Messages</h1>
          <p style={{ color: '#F5A623' }} className="text-sm mt-1">
            {conversations.length} conversation(s)
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-0 sm:px-4 py-0 sm:py-6">
        <div className="bg-white sm:rounded-2xl shadow-sm overflow-hidden flex border-0 sm:border border-gray-100" style={{ height: 'calc(100vh - 52px - env(safe-area-inset-top) - env(safe-area-inset-bottom))', minHeight: '400px' }}>

          {/* Liste conversations */}
          <div className={`${convActive ? 'hidden md:block' : 'block'} w-full md:w-1/3 border-r border-gray-100 overflow-y-auto`}>

            <div style={{ background: '#0A0A0A' }} className="px-4 py-3">
              <p className="text-white text-sm font-bold">Conversations</p>
            </div>

            {loading ? (
              <div className="text-center py-12 text-gray-400 text-sm">Chargement...</div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 text-gray-400 px-4">
                <MessageCircle size={40} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Aucune conversation</p>
              </div>
            ) : (
              conversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => ouvrirConversation(conv)}
                  style={{
                    background: convActive?.id === conv.id ? '#fffbeb' : 'white',
                    borderLeft: convActive?.id === conv.id ? '3px solid #F5A623' : '3px solid transparent'
                  }}
                  className="p-4 border-b border-gray-50 cursor-pointer hover:bg-yellow-50 transition"
                >
                  <div className="flex items-center gap-3">
                    <div style={{ background: '#F5A623' }} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-black text-sm">
                        {conv.prenom?.[0]}{conv.nom?.[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 text-sm">{conv.prenom} {conv.nom}</p>
                      <p className="text-xs text-gray-400 truncate">{conv.annonce_titre}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{conv.contenu}</p>
                    </div>
                    {!conv.lu && user && conv.destinataire_id === user.id && (
                      <div style={{ background: '#F5A623' }} className="w-2 h-2 rounded-full flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Zone messages */}
          <div className={`${convActive ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0`}>
            {!convActive ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <MessageCircle size={60} className="mx-auto mb-4 text-gray-200" />
                  <p className="font-semibold text-gray-400">Sélectionnez une conversation</p>
                  <p className="text-sm text-gray-300 mt-1">Pour voir vos messages</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header conversation */}
                <div style={{ background: '#0A0A0A' }} className="p-4 flex items-center gap-3">
                  <button
                    onClick={() => { setConvActive(null); setMessages([]); }}
                    className="md:hidden text-gray-300 hover:text-yellow-400 transition flex-shrink-0"
                    aria-label="Retour"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div style={{ background: '#F5A623' }} className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-black font-black text-xs">
                      {convActive.prenom?.[0]}{convActive.nom?.[0]}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-bold text-sm truncate">{convActive.prenom} {convActive.nom}</p>
                    <p className="text-gray-400 text-xs truncate">{convActive.annonce_titre}</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${user && msg.expediteur_id === user.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        style={{
                          background: user && msg.expediteur_id === user.id ? '#F5A623' : 'white',
                          color: user && msg.expediteur_id === user.id ? '#000' : '#374151',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                        className="max-w-[75%] sm:max-w-xs px-4 py-2 rounded-2xl text-sm break-words"
                      >
                        {msg.contenu}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Input message */}
                <form onSubmit={handleEnvoyer} className="p-4 border-t border-gray-100 flex gap-3 bg-white">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrire un message..."
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-yellow-400 transition"
                  />
                  <button
                    type="submit"
                    style={{ background: '#F5A623' }}
                    className="text-black px-4 py-2 rounded-xl font-bold hover:opacity-90 transition"
                  >
                    <Send size={18} />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;